import { useEffect, useState } from 'react';
import Layout from '../components/Layout.jsx';
import { api } from '../api.js';

const fmt = (n) => '$ ' + new Intl.NumberFormat('es-CO').format(n ?? 0);
const CAT_EGRESO = [
  { v: 'transferencia_qr', l: 'Transferencia QR', icon: '📲' },
  { v: 'propina', l: 'Propina pagada', icon: '🎁' },
  { v: 'otro_egreso', l: 'Otro egreso', icon: '📤' },
  { v: 'base_caja', l: 'Base en caja (próximo turno)', icon: '🏦' },
  { v: 'entrega', l: 'Total entregado', icon: '✅' },
];

export default function Cuadre() {
  const [turno, setTurno] = useState(null);
  const [cuadre, setCuadre] = useState(null);
  const [egresos, setEgresos] = useState([]);
  const [form, setForm] = useState({ categoria: 'transferencia_qr', concepto: '', monto: '', habitacion_codigo: '' });
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');

  const cargar = async () => {
    const t = await api.turnoAbierto();
    setTurno(t.turno);
    if (t.turno) {
      const [c, e] = await Promise.all([api.cuadre(t.turno.id), api.egresos(t.turno.id)]);
      setCuadre(c);
      setEgresos(e.egresos);
    }
  };

  useEffect(() => { cargar(); }, []);

  const submitEgreso = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMsg('');
    try {
      await api.crearEgreso({
        turno_id: turno?.id ?? null,
        categoria: form.categoria,
        concepto: form.concepto || null,
        monto: Number(form.monto),
        habitacion_codigo: form.habitacion_codigo || null,
      });
      setMsg('✓ Egreso registrado.');
      setForm({ ...form, concepto: '', monto: '', habitacion_codigo: '' });
      await cargar();
      setTimeout(() => setMsg(''), 2500);
    } catch (err) {
      setMsg('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const cerrarTurno = async () => {
    if (!confirm('¿Cerrar el turno? Esta acción no se puede deshacer.')) return;
    setLoading(true);
    try {
      await api.cerrarTurno(turno.id, []);
      window.location.href = '/reportes';
    } catch (err) {
      setMsg('Error al cerrar: ' + err.message);
      setLoading(false);
    }
  };

  if (!turno) {
    return (
      <Layout>
        <div className="card text-center max-w-md mx-auto">
          <div className="text-5xl mb-4">⏸️</div>
          <h2 className="text-2xl font-bold tracking-tight mb-2">Sin turno abierto</h2>
          <p className="text-muted mb-6">Abre un turno desde el dashboard antes de cuadrar.</p>
          <a href="/" className="btn-primary inline-flex">Ir al dashboard</a>
        </div>
      </Layout>
    );
  }

  const t = cuadre?.totales ?? { servicios: 0, productos: 0, egresos: 0, caja_esperada: 0 };
  const egresoBase = (egresos.find((e) => e.categoria === 'base_caja')?.monto) ?? 0;
  const egresoEntrega = (egresos.find((e) => e.categoria === 'entrega')?.monto) ?? 0;

  return (
    <Layout>
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="eyebrow mb-2">Cierre del turno</p>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight">Cuadre de caja</h1>
          <p className="text-muted mt-3">Turno abierto por {turno.usuario_nombre} · {new Date(turno.fecha_apertura).toLocaleString('es-CO')}</p>
        </div>
        <button onClick={() => window.print()} className="btn-secondary print:hidden">🖨️ Imprimir cuadre</button>
      </div>

      {/* Hero totales */}
      <div className="card-ink mb-6 grain relative overflow-hidden">
        <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full opacity-25 blur-3xl pointer-events-none" style={{ background: 'radial-gradient(closest-side, #C8E847, transparent)' }} />
        <div className="relative grid grid-cols-2 md:grid-cols-4 gap-6">
          <KpiInk label="Servicios" valor={t.servicios} />
          <KpiInk label="Inventario" valor={t.productos} />
          <KpiInk label="Egresos" valor={t.egresos} negativo />
          <KpiInk label="Caja esperada" valor={t.caja_esperada} accent />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Detalle servicios + productos */}
        <div className="lg:col-span-3 space-y-4">
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <p className="eyebrow">Servicios vendidos</p>
              <a href="/servicios" className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted hover:text-ink transition">+ Registrar →</a>
            </div>
            {!cuadre?.servicios?.length && <p className="text-muted text-sm">Sin servicios registrados.</p>}
            <div className="space-y-2">
              {cuadre?.servicios?.map((s, i) => (
                <div key={i} className="flex items-center justify-between py-2 px-3 rounded-2xl hover:bg-bone transition">
                  <div>
                    <div className="font-medium tracking-tight text-sm">{s.nombre}</div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted mt-0.5">{s.cantidad} ud</div>
                  </div>
                  <div className="font-mono text-sm font-semibold">{fmt(s.monto)}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <p className="eyebrow">Inventario vendido</p>
              <a href="/movimientos?nuevo=venta" className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted hover:text-ink transition">+ Registrar →</a>
            </div>
            {!cuadre?.productos?.length && <p className="text-muted text-sm">Sin productos vendidos.</p>}
            <div className="space-y-2">
              {cuadre?.productos?.map((p, i) => (
                <div key={i} className="flex items-center justify-between py-2 px-3 rounded-2xl hover:bg-bone transition">
                  <div>
                    <div className="font-medium tracking-tight text-sm">{p.nombre}</div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted mt-0.5">{p.cantidad} ud · {p.categoria?.replace('_', ' ')}</div>
                  </div>
                  <div className="font-mono text-sm font-semibold">{fmt(p.monto)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Egresos */}
        <div className="lg:col-span-2 space-y-4">
          <form onSubmit={submitEgreso} className="card space-y-4">
            <p className="eyebrow">Registrar egreso</p>
            <div>
              <label className="label">Categoría</label>
              <select className="input" value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })}>
                {CAT_EGRESO.map((c) => (
                  <option key={c.v} value={c.v}>{c.icon} {c.l}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Monto</label>
                <input className="input text-center" type="number" min={0} value={form.monto} onChange={(e) => setForm({ ...form, monto: e.target.value })} required />
              </div>
              <div>
                <label className="label">Hab. (opc.)</label>
                <input className="input" value={form.habitacion_codigo} onChange={(e) => setForm({ ...form, habitacion_codigo: e.target.value })} placeholder="H101" />
              </div>
            </div>
            <div>
              <label className="label">Concepto / nota</label>
              <input className="input" value={form.concepto} onChange={(e) => setForm({ ...form, concepto: e.target.value })} placeholder="ej. Nery Arroyo · QR Bancolombia" />
            </div>
            {msg && (
              <div className={`rounded-2xl px-3 py-2 text-xs font-mono ${msg.startsWith('✓') ? 'bg-lime/30 text-ink' : 'bg-red-50 text-red-800 border border-red-200'}`}>{msg}</div>
            )}
            <button className="btn-primary w-full" disabled={loading}>{loading ? '…' : '+ Agregar egreso'}</button>
          </form>

          <div className="card">
            <p className="eyebrow mb-3">Egresos registrados</p>
            {egresos.length === 0 && <p className="text-muted text-sm">Sin egresos.</p>}
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {egresos.map((e) => {
                const cat = CAT_EGRESO.find((x) => x.v === e.categoria);
                return (
                  <div key={e.id} className="flex items-center justify-between py-2 px-3 rounded-2xl bg-bone/60">
                    <div className="min-w-0 flex items-center gap-2">
                      <span className="text-base">{cat?.icon ?? '📤'}</span>
                      <div className="min-w-0">
                        <div className="font-medium tracking-tight text-sm truncate">{cat?.l ?? e.categoria}</div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted truncate">{e.concepto ?? '—'} {e.habitacion_codigo ? '· ' + e.habitacion_codigo : ''}</div>
                      </div>
                    </div>
                    <div className="font-mono text-sm shrink-0">{fmt(e.monto)}</div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="card-lime">
            <p className="eyebrow mb-3">Resumen final</p>
            <div className="space-y-2 font-mono text-sm">
              <Row label="Caja esperada" valor={fmt(t.caja_esperada)} />
              <Row label="Base entregada" valor={fmt(egresoBase)} />
              <Row label="Total entrega" valor={fmt(egresoEntrega)} bold />
              <div className="border-t border-ink/10 pt-2 mt-2">
                <Row label="Diferencia" valor={fmt(t.caja_esperada - egresoBase - egresoEntrega)} bold />
              </div>
            </div>
            <button onClick={cerrarTurno} className="btn-primary w-full mt-5" disabled={loading}>
              Cerrar turno definitivo →
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function KpiInk({ label, valor, accent, negativo }) {
  return (
    <div>
      <div className={`font-mono text-[10px] uppercase tracking-[0.18em] mb-2 ${accent ? 'text-lime' : 'text-paper/60'}`}>{label}</div>
      <div className={`text-2xl md:text-3xl font-semibold tracking-tight ${accent ? 'text-lime' : negativo ? 'text-paper/70' : 'text-paper'}`}>
        {negativo ? '−' : ''}{fmt(Math.abs(valor))}
      </div>
    </div>
  );
}

function Row({ label, valor, bold }) {
  return (
    <div className="flex items-center justify-between">
      <span className={bold ? 'font-semibold text-ink' : 'text-ink/70'}>{label}</span>
      <span className={bold ? 'font-bold text-ink' : 'text-ink/80'}>{valor}</span>
    </div>
  );
}
